import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleLogin } from '../../service/AuthService';
import { toast } from 'react-toastify';

// Reusable SVG Icons for the metrics bar
const TrendUp = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline><polyline points="16 7 22 7 22 13"></polyline></svg>;
const TrendDown = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"></polyline><polyline points="16 17 22 17 22 11"></polyline></svg>;
const ArrowUp = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"></line><polyline points="5 12 12 5 19 12"></polyline></svg>;
const ArrowDown = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><polyline points="19 12 12 19 5 12"></polyline></svg>;
const Star = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>;
const Check = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>;

const SLIDES = [
  {
    id: 1,
    eyebrow: "THE FUTURE OF EDUCATION IS HERE",
    titlePrefix: "Empowering Institutes with ",
    titleHighlight: "PenSense",
    titleSuffix: "",
    content: "Transform your coaching institute with real-time study analytics that give your educators superpowers and boost parent trust.",
    metrics: [
      { top: "Better", bottom: "Results", icon: <TrendUp /> },
      { top: "Parent", bottom: "Trust", icon: <Star /> },
      { top: "Easy", bottom: "Adoption", icon: <Check /> }
    ],
    widget: (
      <div className="flex flex-col gap-4">
        <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-5 shadow-lg">
          <p className="text-sm font-semibold text-slate-400 uppercase tracking-widest pl-2 border-l-2 border-indigo-500">Institute Overview</p>
          <div className="mt-4 flex gap-6">
            <div>
              <p className="text-3xl font-bold text-slate-100">842</p>
              <p className="text-xs text-slate-500 uppercase mt-1">Total Students</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-emerald-400">76%</p>
              <p className="text-xs text-slate-500 uppercase mt-1">Active Today</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900/60 border border-slate-700/50 rounded-2xl p-5 shadow-lg">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest pl-2 border-l-2 border-sky-500 mb-3">Live Heatmap Sync</p>
           <div className="flex gap-1.5 flex-wrap">
             {Array.from({length: 40}).map((_, i) => (
                <div key={i} className={`h-3 w-3 rounded-sm ${Math.random() > 0.3 ? 'bg-sky-500/80 shadow-[0_0_8px_rgba(14,165,233,0.5)]' : 'bg-slate-800'}`} />
             ))}
           </div>
        </div>
      </div>
    )
  },
  {
    id: 2,
    eyebrow: "TRADITIONAL METHODS ARE BROKEN",
    titlePrefix: "The Problem: ",
    titleHighlight: "Reactive Education",
    titleSuffix: "",
    content: "Traditional models rely on exams to identify struggling students, which is often too late to intervene. Self-reported study hours are frequently inaccurate.",
    metrics: [
      { top: "Delayed", bottom: "Feedback", icon: <ArrowDown /> },
      { top: "Last-Minute", bottom: "Failures", icon: <TrendDown /> },
      { top: "Stressed", bottom: "Teachers", icon: <ArrowDown /> }
    ],
    widget: (
      <div className="relative">
         <div className="bg-rose-950/20 border border-rose-900/50 rounded-2xl p-6 shadow-xl relative z-10 backdrop-blur-md">
            <p className="text-rose-400 font-bold mb-4 flex items-center gap-2">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
              Late Discovery
            </p>
            <div className="space-y-4">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-300">Quarterly Mock Exam</span>
                <span className="text-rose-400 font-bold bg-rose-500/10 px-2 py-1 rounded">Score: 45%</span>
              </div>
              <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-rose-500 w-[45%]" />
              </div>
              <p className="text-xs text-slate-500 mt-2">Too late to fix foundational gaps before the final exam.</p>
            </div>
         </div>
      </div>
    )
  },
  {
    id: 3,
    eyebrow: "MOST STUDENTS FALL BEHIND SILENTLY",
    titlePrefix: "Identify Weak Students Before Exams — ",
    titleHighlight: "Automatically",
    titleSuffix: "",
    content: "See who is studying, who is not, and give teachers real-time data to intervene early. Our dashboard automatically highlights 'At-Risk' students who have suddenly dropped their practice hours.",
    metrics: [
      { top: "Better", bottom: "Results", icon: <TrendUp /> },
      { top: "Last-Minute", bottom: "Failures", icon: <ArrowDown /> },
      { top: "Parent", bottom: "Trust", icon: <ArrowUp /> }
    ],
    widget: (
      <div className="flex flex-col gap-4">
         <div className="bg-slate-900/80 border border-slate-700/60 rounded-2xl p-5 shadow-2xl backdrop-blur-xl">
            <p className="text-amber-400 font-bold flex items-center gap-2 text-sm uppercase tracking-wide">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
              6 Students At Risk
            </p>
            <div className="mt-5 border-t border-slate-800 pt-4">
              <div className="flex justify-between items-center">
                <span className="text-slate-200">Math Practice</span>
                <span className="text-rose-400 font-bold flex items-center gap-1"><ArrowDown /> 28%</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">Last Active <span className="text-slate-400 ml-2">3 days ago</span></p>
            </div>
         </div>
         <div className="bg-slate-900/80 border border-slate-700/60 rounded-2xl p-5 shadow-2xl backdrop-blur-xl flex items-center gap-3">
             <span className="text-xl">🔥</span>
             <div>
               <p className="text-sm font-semibold text-slate-200">Top Performer: <span className="text-emerald-400">+145 mins</span></p>
               <p className="text-xs text-slate-500">today</p>
             </div>
         </div>
      </div>
    )
  },
  {
    id: 4,
    eyebrow: "NO MORE GUESSWORK",
    titlePrefix: "Data-Driven ",
    titleHighlight: "Personalized Mentoring",
    titleSuffix: "",
    content: "Click on any student to instantly view their comprehensive profile: most practiced subjects, neglect areas, a consistency heatmap, and a feed of their latest 50 micro-chapter practice sessions.",
    metrics: [
      { top: "Accurate", bottom: "Tracking", icon: <Check /> },
      { top: "Subject", bottom: "Specific", icon: <Star /> },
      { top: "Proactive", bottom: "Help", icon: <TrendUp /> }
    ],
    widget: (
      <div className="bg-slate-900/80 border border-slate-700/60 rounded-2xl p-6 shadow-2xl backdrop-blur-xl">
         <div className="flex items-center gap-4 mb-6">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500 to-sky-400 flex items-center justify-center text-lg font-bold text-slate-950">
              RS
            </div>
            <div>
              <p className="text-lg font-bold text-white">Rahul Singh</p>
              <p className="text-xs text-slate-400 font-mono">ID: PEN-8924</p>
            </div>
         </div>
         <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-950/50 p-3 rounded-xl border border-slate-800">
               <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest">Top Subject</p>
               <p className="text-sky-400 font-bold mt-1">Physics</p>
            </div>
            <div className="bg-slate-950/50 p-3 rounded-xl border border-slate-800">
               <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest">Total Study</p>
               <p className="text-emerald-400 font-bold mt-1">1,240 <span className="text-xs font-normal text-slate-500">min</span></p>
            </div>
         </div>
      </div>
    )
  },
  {
    id: 5,
    eyebrow: "AI-POWERED ASSISTANT",
    titlePrefix: "Your Smart ",
    titleHighlight: "Class Co-Pilot",
    titleSuffix: "",
    content: "Our AI actively analyzes the class's overall performance, comparing averages and identifying lagging subjects to generate practical suggestions for your teachers.",
    metrics: [
      { top: "Automated", bottom: "Insights", icon: <Star /> },
      { top: "Focus", bottom: "Recommendations", icon: <Check /> },
      { top: "Time", bottom: "Saved", icon: <TrendUp /> }
    ],
    widget: (
       <div className="bg-indigo-950/30 border border-indigo-500/30 rounded-2xl p-6 shadow-[0_0_40px_rgba(99,102,241,0.15)] relative backdrop-blur-md">
          <div className="absolute -top-4 -left-4 bg-indigo-500 h-10 w-10 rounded-full flex items-center justify-center shadow-lg shadow-indigo-500/50">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.9 1.2 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/></svg>
          </div>
          <h3 className="text-indigo-400 font-bold ml-4 mb-3">Actionable AI Suggestion</h3>
          <p className="text-sm text-slate-300 leading-relaxed italic border-l-2 border-indigo-500/50 pl-3">
            "Class average for <span className="text-indigo-300 font-semibold">'Organic Chemistry'</span> has dropped by 15% this week. Focus this week's assignments on Chemistry to bridge the gap."
          </p>
       </div>
    )
  },
  {
    id: 6,
    eyebrow: "TRANSPARENT COMMUNICATION",
    titlePrefix: "Effortless Reporting ",
    titleHighlight: "For Parents",
    titleSuffix: "",
    content: "One-Click CSV Export instantly turns raw analytics into a clean, downloadable spreadsheet. Attach these reports to parent communications for undeniable, objective proof of a student’s effort.",
    metrics: [
      { top: "Objective", bottom: "Proof", icon: <Check /> },
      { top: "1-Click", bottom: "Export", icon: <TrendUp /> },
      { top: "Higher", bottom: "Satisfaction", icon: <Star /> }
    ],
    widget: (
      <div className="flex flex-col items-center justify-center gap-4 bg-slate-900/60 border border-slate-700/50 rounded-2xl p-8 shadow-2xl backdrop-blur-md">
         <div className="h-16 w-16 bg-emerald-500/20 border border-emerald-500/50 rounded-2xl flex items-center justify-center text-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.3)]">
           <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M8 13h2"/><path d="M8 17h2"/><path d="M14 13h2"/><path d="M14 17h2"/></svg>
         </div>
         <p className="font-semibold text-slate-200 text-center">Export CSV Report</p>
         <div className="bg-slate-950 px-4 py-2 rounded-lg border border-slate-800 flex items-center gap-2">
           <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg>
           <span className="text-xs font-mono text-slate-400">Class_Report_May.csv</span>
         </div>
      </div>
    )
  },
  {
    id: 7,
    eyebrow: "ZERO GRADING",
    titlePrefix: "Validate Homework ",
    titleHighlight: "Automatically",
    titleSuffix: "",
    content: "Assign 2 hours of Physics practice, then log into the dashboard the next day to verify exactly which students completed their mandatory 'pen-time' without manually grading a single sheet.",
    metrics: [
      { top: "100%", bottom: "Accountability", icon: <Check /> },
      { top: "Zero", bottom: "Manual Checking", icon: <TrendUp /> },
      { top: "True", bottom: "Study Hours", icon: <Star /> }
    ],
    widget: (
       <div className="bg-slate-900/80 border border-slate-700/60 rounded-2xl p-6 shadow-2xl backdrop-blur-xl w-full">
         <h3 className="text-sm font-bold text-slate-300 mb-4 uppercase tracking-widest">Homework Status</h3>
         <div className="bg-slate-950/60 rounded-xl p-4 border border-slate-800">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sky-300 font-semibold text-sm">Physics Assignment (2 hrs)</span>
              <span className="text-slate-400 text-xs font-mono">45 / 50</span>
            </div>
            <div className="h-2 w-full bg-slate-800 rounded-full overflow-hidden">
               <div className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 w-[90%]" />
            </div>
            <p className="text-xs text-emerald-400 mt-3 font-semibold flex items-center gap-1">
              <Check /> 90% Completion Rate
            </p>
         </div>
       </div>
    )
  },
  {
    id: 8,
    eyebrow: "BRAND DIFFERENTIATION",
    titlePrefix: "Boost Institutional ",
    titleHighlight: "Prestige",
    titleSuffix: "",
    content: "Utilize IoT smart-pen technology combined with a glassmorphic analytics dashboard to stand out from competitors and show parents you invest in cutting-edge education.",
    metrics: [
      { top: "Modern", bottom: "Branding", icon: <Star /> },
      { top: "Tech-Forward", bottom: "Image", icon: <Check /> },
      { top: "Competitive", bottom: "Edge", icon: <TrendUp /> }
    ],
    widget: (
      <div className="flex items-center justify-center p-8 bg-gradient-to-br from-indigo-900/40 to-slate-900/80 border border-indigo-500/30 rounded-full h-48 w-48 mx-auto shadow-[0_0_50px_rgba(99,102,241,0.2)] relative">
         <div className="absolute inset-0 bg-indigo-500/20 blur-2xl rounded-full animate-pulse-slow"></div>
         <div className="text-center relative z-10">
           <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="url(#gradient)" strokeWidth="1.5" className="mx-auto mb-2"><defs><linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#818cf8" /><stop offset="100%" stopColor="#38bdf8" /></linearGradient></defs><path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"/></svg>
           <p className="font-bold text-indigo-300 uppercase tracking-widest text-xs">PenSense</p>
           <p className="font-semibold text-slate-100 text-sm mt-1">Partner Institute</p>
         </div>
      </div>
    )
  },
  {
    id: 9,
    eyebrow: "NEW REVENUE STREAMS",
    titlePrefix: "Offer Premium ",
    titleHighlight: "Mentorship Tiers",
    titleSuffix: "",
    content: "Institutes can offer 'Premium Mentorship' programs where specific students receive smart-pen tracking, justifying higher enrollment fees with exclusive, high-tech data insights.",
    metrics: [
      { top: "Higher", bottom: "Revenue", icon: <TrendUp /> },
      { top: "Elite", bottom: "Tracking", icon: <Star /> },
      { top: "Exclusive", bottom: "Insights", icon: <Check /> }
    ],
    widget: (
      <div className="flex flex-col gap-3 w-full">
         <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-4 flex justify-between items-center decoration-slate-500 opacity-60">
            <div>
              <p className="text-sm font-semibold text-slate-300">Standard Mentorship</p>
              <p className="text-xs text-slate-500 mt-0.5">Basic classes & tests</p>
            </div>
            <p className="text-slate-400 font-bold">Base Fee</p>
         </div>
         <div className="bg-gradient-to-r from-indigo-900/50 to-sky-900/30 border border-indigo-500/50 rounded-xl p-4 flex justify-between items-center shadow-[0_0_20px_rgba(99,102,241,0.2)]">
            <div>
              <p className="text-sm font-bold text-white flex items-center gap-2">
                Premium Mentorship <Star />
              </p>
              <p className="text-xs text-indigo-300 mt-0.5">PenSense IoT Tracking + AI Insights</p>
            </div>
            <p className="text-emerald-400 font-bold bg-emerald-500/10 px-2 py-1 rounded">Base + ₹XX</p>
         </div>
      </div>
    )
  },
  {
    id: 10,
    eyebrow: "TAKE THE NEXT STEP",
    titlePrefix: "Join the Future of ",
    titleHighlight: "Education",
    titleSuffix: "",
    content: "Ready to equip your teachers with PenSense? Take your coaching institute to the next level today. Stop guessing and start measuring.",
    metrics: [
      { top: "Instant", bottom: "Setup", icon: <Check /> },
      { top: "Dedicated", bottom: "Support", icon: <Star /> },
      { top: "Proven", bottom: "ROI", icon: <TrendUp /> }
    ],
    isCallToAction: true,
    widget: (
      <div className="flex items-center justify-center p-6 h-full w-full">
         <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="text-sky-500/40">
           <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
         </svg>
      </div>
    )
  }
];

export default function SchoolPresentation() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const navigate = useNavigate();

  const handleNext = () => {
    if (currentSlide < SLIDES.length - 1) {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const doAutoLogin = async () => {
    try {
      setIsLoggingIn(true);
      const res = await handleLogin("institute@gmail.com", "admin@123");
      if (res.status === "success") {
        localStorage.setItem("user", JSON.stringify(res));
        toast.success("Welcome to your Demo Dashboard!");
        navigate("/dashboard");
      } else {
        toast.error(res.message || "Auto-login failed. Redirecting to login...");
        navigate("/");
      }
    } catch (err) {
      toast.error("Auto-login failed. Redirecting...");
      navigate("/");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const slide = SLIDES[currentSlide];

  return (
    <div className="h-screen bg-[#050B14] text-slate-100 flex flex-col font-sans overflow-hidden relative">
      
      {/* Intense Background Nebula Effects */}
      <div className="absolute top-[-20%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-indigo-900/20 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[40vw] h-[40vw] rounded-full bg-sky-900/20 blur-[100px] pointer-events-none" />
      
      {/* Top Gradient Bar */}
      <div className="h-1 w-full bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-400 shrink-0 relative z-20" />
      
      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between shrink-0 h-[60px] relative z-20">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex shrink-0 h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-sky-400 text-[#050B14] font-bold shadow-lg shadow-sky-500/20 text-sm">
            P
          </div>
          <div className="min-w-0">
            <span className="font-bold text-base tracking-[0.15em] text-slate-100 uppercase block leading-none truncate">Pensense</span>
          </div>
        </div>
        <button 
          onClick={() => navigate('/')}
          className="text-xs shrink-0 font-medium text-slate-400 hover:text-slate-100 transition-colors border border-slate-800 rounded-full px-4 py-1.5 hover:bg-slate-900"
        >
          Exit Presentation
        </button>
      </header>

      {/* Main Content Layout */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-2 md:py-4 flex items-center relative z-10 transition-all duration-500 animate-in fade-in zoom-in-95" key={slide.id}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 w-full items-center">
          
          {/* Left Column: Text & Metrics */}
          <div className="flex flex-col mt-[-2rem]">
            <h3 className="text-[10px] md:text-xs font-bold tracking-[0.25em] text-sky-400 uppercase mb-3 text-shadow-sm">
              {slide.eyebrow}
            </h3>
            
            <h1 className="text-3xl md:text-5xl lg:text-[54px] font-bold tracking-tight text-white mb-5 leading-tight lg:leading-[1.1]">
              {slide.titlePrefix} 
              <span className="text-sky-400 font-extrabold">{slide.titleHighlight}</span>
              {slide.titleSuffix}
            </h1>
            
            <p className="text-lg md:text-xl lg:text-[22px] text-slate-300 leading-snug font-light mb-8 max-w-xl">
              {slide.content}
            </p>

            {/* Glassmorphic Metrics Bar */}
            <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700/50 rounded-xl p-3 flex flex-wrap lg:flex-nowrap gap-3 md:gap-0 justify-between shadow-[0_20px_40px_-10px_rgba(0,0,0,0.5)] w-full max-w-xl relative overflow-hidden">
              <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-sky-500/50 to-transparent"></div>
              
              {slide.metrics.map((metric, idx) => (
                <div key={idx} className={`flex items-center gap-2 px-3 ${idx !== slide.metrics.length - 1 ? 'md:border-r border-slate-800' : ''}`}>
                  <div className="shrink-0 scale-110">{metric.icon}</div>
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-slate-200">{metric.top}</span>
                    <span className="text-[11px] text-slate-400 uppercase tracking-wide">{metric.bottom}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Widgets & CTA */}
          <div className="flex flex-col items-center justify-center w-full max-w-md mx-auto lg:mx-0 lg:ml-auto relative min-h-[300px]">
            {slide.widget}
            
            {/* Auto Login CTA for the last slide, rendered in the right column */}
            {slide.isCallToAction && (
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <button 
             onClick={doAutoLogin}
             disabled={isLoggingIn}
             className="bg-sky-500 hover:bg-sky-400 text-[#050B14] font-bold tracking-wide py-3 px-8 rounded-full shadow-[0_0_20px_rgba(14,165,233,0.5)] transition-all hover:scale-105 hover:shadow-[0_0_40px_rgba(14,165,233,0.8)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-base"
            >
             {isLoggingIn ? "Logging into Demo..." : "View dashboard"}
            </button>
          </div>
            )}
          </div>
        </div>
      </main>

      {/* Floating Navigation Arrows - Fixed to screen edges */}
      {currentSlide > 0 && (
        <button 
          onClick={handlePrev}
          className="fixed left-2 md:left-6 lg:left-8 top-1/2 -translate-y-1/2 p-2 md:p-3 text-slate-500 hover:text-white transition-all hover:scale-110 z-50 bg-slate-900/40 hover:bg-slate-800/80 rounded-full backdrop-blur-md shadow-lg"
          aria-label="Previous Slide"
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="md:w-10 md:h-10"><path d="m15 18-6-6 6-6"/></svg>
        </button>
      )}
      {currentSlide < SLIDES.length - 1 && (
        <button 
          onClick={handleNext}
          className="fixed right-2 md:right-6 lg:right-8 top-1/2 -translate-y-1/2 p-2 md:p-3 text-slate-500 hover:text-sky-400 transition-all hover:scale-110 z-50 bg-slate-900/40 hover:bg-slate-800/80 rounded-full backdrop-blur-md shadow-lg"
          aria-label="Next Slide"
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="md:w-10 md:h-10"><path d="m9 18 6-6-6-6"/></svg>
        </button>
      )}

      {/* Footer / Progress */}
      <footer className="shrink-0 flex flex-col items-center justify-center px-8 pb-3 relative z-20 gap-2 mt-auto">
        <div className="flex flex-col items-center gap-1">
          <p className="text-xs font-semibold text-slate-200">Start a Pilot in Your Institute</p>
          <div className="flex items-center gap-4 text-[11px] text-sky-400/80 font-medium">
             <span className="flex items-center gap-1"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg> 9819575898</span>
             <span className="text-slate-700">|</span>
             <span className="flex items-center gap-1"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/></svg> pensense.tech</span>
          </div>
        </div>

        <div className="flex gap-2.5 mt-1">
          {SLIDES.map((s, index) => (
            <button
              key={s.id}
              onClick={() => setCurrentSlide(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? "w-8 bg-sky-400" 
                  : "w-2 bg-slate-800 hover:bg-slate-600"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </footer>
    </div>
  );
}
